import {
  createContext, useCallback, useContext, useEffect, useState,
  type ReactNode,
} from 'react';

// Each language is a tree of string keys. The translation function `t()`
// supports either a top-level key or a nested key inside a group:
//   t('siteTitle')                    → "RetroGameUp"
//   t('partners', 'pokemonText')      → "New to Pokémon GO…"
type Group = Record<string, string>;
type Bundle = Record<string, string | Group>;

const dict: Record<'en' | 'fr', Bundle> = {
  en: {
    siteTitle: 'RetroGameUp',
    siteSubtitle: 'Tool-assisted videos for your entertainment!',
    categories: {
      playthrough: 'Playthrough',
      charPlaythrough: 'Character Playthrough',
      sportPlaythrough: 'Sport Playthrough',
      doomPlaythrough: 'Doom Playthrough',
      superplay: 'Superplay',
      compilation: 'Compilation',
      'playthrough-nes': 'Playthrough: NES',
      'playthrough-snes': 'Playthrough: Super Nintendo',
      'playthrough-n64': 'Playthrough: Nintendo 64',
      'playthrough-atari': 'Playthrough: Atari 2600',
      'playthrough-intellivision': 'Playthrough: Intellivision',
      'playthrough-sega-cd': 'Playthrough: Sega CD',
      'playthrough-sega-32x': 'Playthrough: Sega 32X',
      'playthrough-sega-genesis': 'Playthrough: Sega Genesis',
      'playthrough-game-boy': 'Playthrough: Game Boy',
      'playthrough-sega-master-system': 'Playthrough: Sega Master System',
      'playthrough-playstation': 'Playthrough: PlayStation',
      'playthrough-turbografx-16': 'Playthrough: TurboGrafx-16',
    },
    playthroughSubs: {
      playthrough: 'All Playthroughs',
      'playthrough-nes': 'NES',
      'playthrough-snes': 'Super Nintendo',
      'playthrough-n64': 'Nintendo 64',
      'playthrough-atari': 'Atari 2600',
      'playthrough-intellivision': 'Intellivision',
      'playthrough-sega-cd': 'Sega CD',
      'playthrough-sega-32x': 'Sega 32X',
      'playthrough-sega-genesis': 'Sega Genesis',
      'playthrough-game-boy': 'Game Boy',
      'playthrough-sega-master-system': 'Sega Master System',
      'playthrough-playstation': 'PlayStation',
      'playthrough-turbografx-16': 'TurboGrafx-16',
    },
    categoryDescriptions: {
      playthrough: 'Full playthroughs of classic games from start to finish.',
      'playthrough-nes': 'NES playthroughs and full game completions.',
      'playthrough-snes': 'Super Nintendo playthroughs and full game completions.',
      'playthrough-n64': 'Nintendo 64 playthroughs and full game completions.',
      'playthrough-atari': 'Atari 2600 playthroughs and full game completions.',
      'playthrough-intellivision': 'Intellivision playthroughs and full game completions.',
      'playthrough-sega-cd': 'Sega CD playthroughs and full game completions.',
      'playthrough-sega-32x': 'Sega 32X playthroughs and full game completions.',
      'playthrough-sega-genesis': 'Sega Genesis playthroughs and full game completions.',
      'playthrough-game-boy': 'Game Boy playthroughs and full game completions.',
      'playthrough-sega-master-system': 'Sega Master System playthroughs and full game completions.',
      'playthrough-playstation': 'PlayStation playthroughs and full game completions.',
      'playthrough-turbografx-16': 'TurboGrafx-16 playthroughs and full game completions.',
      charPlaythrough: "Single-character playthroughs in fighting and beat 'em up games.",
      sportPlaythrough: 'Sports game matchups — full games from start to finish.',
      doomPlaythrough: 'Doom level-by-level playthroughs across Super NES, Sega Saturn, and Sega 32X.',
      superplay: 'High-skill runs and superplays showcasing expert gameplay.',
      compilation: 'Best-of compilations, highlights, and retro gaming collections.',
    },
    allCharPlaythroughs: 'All Character Playthroughs',
    allSportPlaythroughs: 'All Sport Playthroughs',
    cookieBanner: {
      ariaLabel: 'Cookie consent',
      message: 'We use cookies and local storage for language preferences, video caching, and Google Analytics. See our',
      accept: 'Accept',
      decline: 'Decline',
    },
    cookiePolicyLink: 'Cookie Policy',
    privacyPolicyLink: 'Privacy Policy',
    privacyPolicy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: May 2026',
      intro: 'RetroGameUp is committed to protecting your privacy. This page explains what personal data may be collected and how it is handled. This site is a hobby project and does not collect personal data for commercial purposes.',
      emailTitle: 'Contact by Email',
      emailBody: 'If you reach out via the email link on this site, your message and email address will be used solely to respond to your inquiry. We do not store, share, or use your contact details for any other purpose. Emails are handled personally by the site owner.',
      externalLinksTitle: 'Links to External Websites',
      externalLinksBody: 'This site links to YouTube and other third-party platforms. Once you leave RetroGameUp, their own privacy policies apply. We have no control over the content or data practices of external sites and are not responsible for them. We recommend reviewing the privacy policy of any site you visit.',
      analyticsTitle: 'Google Analytics',
      analyticsBody: 'This site uses Google Analytics to analyse visitor behaviour in an anonymous and aggregated way. No personally identifiable information is collected. Data includes pages visited, session duration, and approximate geographic location. You can opt out at any time using the Google Analytics Opt-out Browser Add-on available from Google.',
      advertisingTitle: 'Advertising & Affiliate Links',
      advertisingBody: 'Some links on this site (TubeBuddy, NordVPN) are affiliate links. Clicking them may earn RetroGameUp a small commission at no extra cost to you. These are third-party services with their own privacy policies. We do not serve banner ads or use ad networks that track you across the web.',
      contactTitle: 'Questions',
      contactBody: 'If you have any questions about this Privacy Policy, you can contact us via the email link in the header.',
    },
    cookiePolicy: {
      title: 'Cookie & Storage Policy',
      lastUpdated: 'Last updated: May 2026',
      intro: 'This page explains what data RetroGameUp stores in your browser and why. We aim to keep this as minimal as possible.',
      langPrefTitle: 'Language Preference',
      langPrefBody: "When you switch between English and French, your choice is stored in your browser's local storage so the site remembers it on your next visit. No personal data is collected — only the value \"en\" or \"fr\".",
      videoCacheTitle: 'Video Data Cache',
      videoCacheBody: "To reduce the number of calls to the YouTube API and speed up load times, video data (titles, thumbnails, categories) is temporarily cached in your browser's local storage for up to 2 hours. This data comes entirely from YouTube and contains no personal information.",
      youtubeTitle: 'YouTube',
      youtubeBody: "RetroGameUp uses the YouTube Data API v3 to fetch video information, and embeds YouTube videos directly on the site. When you play a video, YouTube may set its own cookies and collect data in accordance with Google's privacy policy. RetroGameUp has no control over these cookies.",
      youtubeNotePre: 'To learn more, visit ',
      youtubeNoteGoogleLink: "Google's Privacy Policy",
      youtubeNoteMid: ' and ',
      youtubeNoteYouTubeLink: "YouTube's Terms of Service",
      youtubeNotePost: '.',
      analyticsTitle: 'Google Analytics',
      analyticsBody: 'This site uses Google Analytics to better understand how visitors use it. Google Analytics collects anonymous usage data such as pages visited, time spent on the site, and general geographic region. This data is aggregated and does not identify you personally. Google may set cookies to support this tracking. You can opt out at any time using the Google Analytics Opt-out Browser Add-on.',
      managingTitle: 'Managing Your Data',
      managingBody: "You can clear all locally stored data at any time through your browser's developer tools (Application → Local Storage → clear) or via your browser's privacy settings. Doing so will reset your language preference and clear the video cache.",
    },
    latestVideos: 'Latest Videos',
    backToVideos: '← Back to videos',
    loading: 'Loading video data…',
    error: 'Error',
    noVideos: 'No videos found for',
    videoNotFound: 'Video not found.',
    refresh: {
      button: 'Refresh',
      tooltip: 'Re-fetch videos from YouTube (uses API quota)',
      confirm: 'Refresh videos from YouTube?\n\nThis uses your daily YouTube API quota. Only do this when new videos were uploaded.',
      working: 'Refreshing…',
      success: 'Updated — {n} video(s).',
      neverUpdated: 'Never updated from API',
      updated: 'Last updated',
    },
    footer: '© RetroGameUp™ 2026, All video game characters, titles, artwork, music, and other creative elements of these games belong solely to their original creators. RetroGameUp is not affiliated any video game publisher or development company. General identity, web design and text content © RetroGameUp™',
    language: 'Language',
    partners: {
      pokemonText: "New to Pokémon GO or haven't played in 90 days? Use Retrogameup's referral code to get special rewards!",
      tubeBuddyText: 'Get TubeBuddy to access the same powerful tools that top YouTubers use to thrive!',
      tubeBuddyLink: 'tubebuddy.com',
      nordVpnText: "Get 74% off NordVPN's 2-year plan + 3 extra months!",
      nordVpnLink: 'Get the deal',
    },
  },
  fr: {
    siteTitle: 'RetroGameUp',
    siteSubtitle: 'Vidéos assistées par outils pour votre divertissement !',
    categories: {
      playthrough: 'Jeu complet',
      charPlaythrough: 'Jeu complété par personnage',
      sportPlaythrough: 'Jeu sportif',
      doomPlaythrough: 'Doom par niveau',
      superplay: 'Superplay',
      compilation: 'Compilation',
      'playthrough-nes': 'Jeu complet : NES',
      'playthrough-snes': 'Jeu complet : Super Nintendo',
      'playthrough-n64': 'Jeu complet : Nintendo 64',
      'playthrough-atari': 'Jeu complet : Atari 2600',
      'playthrough-intellivision': 'Jeu complet : Intellivision',
      'playthrough-sega-cd': 'Jeu complet : Sega CD',
      'playthrough-sega-32x': 'Jeu complet : Sega 32X',
      'playthrough-sega-genesis': 'Jeu complet : Sega Genesis',
      'playthrough-game-boy': 'Jeu complet : Game Boy',
      'playthrough-sega-master-system': 'Jeu complet : Sega Master System',
      'playthrough-playstation': 'Jeu complet : PlayStation',
      'playthrough-turbografx-16': 'Jeu complet : TurboGrafx-16',
    },
    playthroughSubs: {
      playthrough: 'Tous les jeux complets',
      'playthrough-nes': 'NES',
      'playthrough-snes': 'Super Nintendo',
      'playthrough-n64': 'Nintendo 64',
      'playthrough-atari': 'Atari 2600',
      'playthrough-intellivision': 'Intellivision',
      'playthrough-sega-cd': 'Sega CD',
      'playthrough-sega-32x': 'Sega 32X',
      'playthrough-sega-genesis': 'Sega Genesis',
      'playthrough-game-boy': 'Game Boy',
      'playthrough-sega-master-system': 'Sega Master System',
      'playthrough-playstation': 'PlayStation',
      'playthrough-turbografx-16': 'TurboGrafx-16',
    },
    categoryDescriptions: {
      playthrough: 'Jeux complet du début à la fin.',
      'playthrough-nes': 'Jeux complets sur NES du début à la fin.',
      'playthrough-snes': 'Jeux complets sur Super Nintendo du début à la fin.',
      'playthrough-n64': 'Jeux complets sur Nintendo 64 du début à la fin.',
      'playthrough-atari': 'Jeux complets sur Atari 2600 du début à la fin.',
      'playthrough-intellivision': 'Jeux complets sur Intellivision du début à la fin.',
      'playthrough-sega-cd': 'Jeux complets sur Sega CD du début à la fin.',
      'playthrough-sega-32x': 'Jeux complets sur Sega 32X du début à la fin.',
      'playthrough-sega-genesis': 'Jeux complets sur Sega Genesis du début à la fin.',
      'playthrough-game-boy': 'Jeux complets sur Game Boy du début à la fin.',
      'playthrough-sega-master-system': 'Jeux complets sur Sega Master System du début à la fin.',
      'playthrough-playstation': 'Jeux complets sur PlayStation du début à la fin.',
      'playthrough-turbografx-16': 'Jeux complets sur TurboGrafx-16 du début à la fin.',
      charPlaythrough: 'Jeux de personnage complets dans les jeux de combat et de baston.',
      sportPlaythrough: 'Vidéo de match de jeu entre 2 équipes complet.',
      doomPlaythrough: 'Doom niveau par niveau sur Super NES, Sega Saturn et Sega 32X.',
      superplay: 'Jeu complet en utilisant les outils de vidéos assistées au maximum!',
      compilation: 'Compilations des meilleurs moments et collections de jeux rétro.',
    },
    allCharPlaythroughs: 'Tous les jeux de personnages',
    allSportPlaythroughs: 'Tous les jeux sportifs',
    cookieBanner: {
      ariaLabel: 'Consentement aux cookies',
      message: 'Nous utilisons des cookies et le stockage local pour les préférences de langue, la mise en cache des vidéos et Google Analytics. Consultez notre',
      accept: 'Accepter',
      decline: 'Refuser',
    },
    cookiePolicyLink: 'Politique de cookies',
    privacyPolicyLink: 'Politique de confidentialité',
    privacyPolicy: {
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour : mai 2026',
      intro: "RetroGameUp s'engage à protéger votre vie privée. Cette page explique quelles données personnelles peuvent être collectées et comment elles sont traitées. Ce site est un projet personnel et ne collecte pas de données personnelles à des fins commerciales.",
      emailTitle: 'Contact par courriel',
      emailBody: "Si vous nous contactez via le lien courriel du site, votre message et votre adresse électronique seront utilisés uniquement pour répondre à votre demande. Nous ne stockons pas, ne partageons pas et n'utilisons pas vos coordonnées à d'autres fins. Les courriels sont traités personnellement par le propriétaire du site.",
      externalLinksTitle: 'Liens vers des sites externes',
      externalLinksBody: "Ce site contient des liens vers YouTube et d'autres plateformes tierces. Dès que vous quittez RetroGameUp, leurs propres politiques de confidentialité s'appliquent. Nous n'avons aucun contrôle sur le contenu ou les pratiques de données des sites externes et n'en sommes pas responsables. Nous vous recommandons de consulter la politique de confidentialité de tout site que vous visitez.",
      analyticsTitle: 'Google Analytics',
      analyticsBody: "Ce site utilise Google Analytics pour analyser le comportement des visiteurs de manière anonyme et agrégée. Aucune information permettant de vous identifier personnellement n'est collectée. Les données comprennent les pages visitées, la durée des sessions et la localisation géographique approximative. Vous pouvez vous y opposer à tout moment en utilisant le module complémentaire de navigateur de désinscription de Google Analytics, disponible auprès de Google.",
      advertisingTitle: "Publicité et liens d'affiliation",
      advertisingBody: "Certains liens sur ce site (TubeBuddy, NordVPN) sont des liens d'affiliation. En cliquant dessus, RetroGameUp peut percevoir une petite commission sans frais supplémentaires pour vous. Ces services tiers ont leurs propres politiques de confidentialité. Nous n'affichons pas de bannières publicitaires et n'utilisons pas de réseaux publicitaires qui vous suivent sur le web.",
      contactTitle: 'Questions',
      contactBody: "Si vous avez des questions concernant cette politique de confidentialité, vous pouvez nous contacter via le lien courriel dans l'en-tête.",
    },
    cookiePolicy: {
      title: 'Politique de cookies et de stockage',
      lastUpdated: 'Dernière mise à jour : mai 2026',
      intro: 'Cette page explique quelles données RetroGameUp stocke dans votre navigateur et pourquoi. Nous cherchons à les limiter au strict nécessaire.',
      langPrefTitle: 'Préférence de langue',
      langPrefBody: "Lorsque vous changez de langue entre l'anglais et le français, votre choix est sauvegardé dans le stockage local de votre navigateur afin que le site s'en souvienne lors de votre prochaine visite. Aucune donnée personnelle n'est collectée — uniquement la valeur « en » ou « fr ».",
      videoCacheTitle: 'Cache des données vidéo',
      videoCacheBody: "Pour réduire le nombre d'appels à l'API YouTube et accélérer le chargement, les données vidéo (titres, miniatures, catégories) sont temporairement mises en cache dans le stockage local de votre navigateur pendant 2 heures maximum. Ces données proviennent entièrement de YouTube et ne contiennent aucune information personnelle.",
      youtubeTitle: 'YouTube',
      youtubeBody: "RetroGameUp utilise l'API YouTube Data v3 pour récupérer les informations vidéo et intègre des vidéos YouTube directement sur le site. Lorsque vous lisez une vidéo, YouTube peut déposer ses propres cookies et collecter des données conformément à la politique de confidentialité de Google. RetroGameUp n'a aucun contrôle sur ces cookies.",
      youtubeNotePre: 'Pour en savoir plus, consultez la ',
      youtubeNoteGoogleLink: 'Politique de confidentialité de Google',
      youtubeNoteMid: ' et les ',
      youtubeNoteYouTubeLink: "Conditions d'utilisation de YouTube",
      youtubeNotePost: '.',
      analyticsTitle: 'Google Analytics',
      analyticsBody: "Ce site utilise Google Analytics afin de mieux comprendre la manière dont les visiteurs l'utilisent. Google Analytics collecte des données d'utilisation anonymes telles que les pages visitées, le temps passé sur le site et la région géographique générale. Ces données sont agrégées et ne permettent pas de vous identifier personnellement. Google peut déposer des cookies pour assurer ce suivi. Vous pouvez vous y opposer à tout moment en utilisant le module complémentaire de navigateur de désinscription de Google Analytics.",
      managingTitle: 'Gérer vos données',
      managingBody: 'Vous pouvez effacer toutes les données stockées localement à tout moment via les outils de développement de votre navigateur (Application → Stockage local → effacer) ou via les paramètres de confidentialité de votre navigateur. Cela réinitialisera votre préférence de langue et effacera le cache vidéo.',
    },
    latestVideos: 'Dernières vidéos',
    backToVideos: '← Retour aux vidéos',
    loading: 'Chargement des données vidéo…',
    error: 'Erreur',
    noVideos: 'Aucune vidéo trouvée pour',
    videoNotFound: 'Vidéo introuvable.',
    refresh: {
      button: 'Actualiser',
      tooltip: "Recharger les vidéos depuis YouTube (consomme du quota API)",
      confirm: "Actualiser les vidéos depuis YouTube ?\n\nCela consomme votre quota quotidien de l'API YouTube. À ne faire que lorsque de nouvelles vidéos ont été publiées.",
      working: 'Actualisation…',
      success: 'Mis à jour — {n} vidéo(s).',
      neverUpdated: "Jamais actualisé depuis l'API",
      updated: 'Dernière mise à jour',
    },
    footer: "© RetroGameUp™ 2026, Tous les personnages, titres, œuvres, musiques et autres éléments créatifs de ces jeux appartiennent exclusivement à leurs créateurs d'origine. RetroGameUp n'est affilié à aucun éditeur ou développeur de jeux vidéo. Identité générale, conception web et contenu textuel © RetroGameUp™",
    language: 'Langue',
    partners: {
      pokemonText: "Nouveau sur Pokémon GO ou vous n'avez pas joué depuis 90 jours? Utilisez le code de parrainage de Retrogameup pour obtenir des récompenses spéciales!",
      tubeBuddyText: "Obtenez TubeBuddy pour accéder aux mêmes outils puissants qu'utilisent les meilleurs YouTubers!",
      tubeBuddyLink: 'tubebuddy.com',
      nordVpnText: 'Obtenez 74% de réduction sur le forfait 2 ans de NordVPN + 3 mois supplémentaires!',
      nordVpnLink: "Profiter de l'offre",
    },
  },
};

type Lang = keyof typeof dict;
const STORAGE_KEY = 'retrogameup_lang';

interface LanguageCtx {
  language: Lang;
  setLanguage: (l: Lang) => void;
  t: (key: string, sub?: string) => string;
}

const LangContext = createContext<LanguageCtx | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Lang>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'fr' ? 'fr' : 'en';
  });

  // Persist + sync <html lang="…">.
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const t = useCallback(
    (key: string, sub?: string): string => {
      const bundle = dict[language];
      if (!bundle) return key;
      const entry = bundle[key];
      if (sub && entry && typeof entry === 'object') {
        return (entry as Group)[sub] ?? key;
      }
      return typeof entry === 'string' ? entry : key;
    },
    [language],
  );

  return (
    <LangContext.Provider value={{ language, setLanguage: setLanguageState, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLanguage(): LanguageCtx {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
